import type { PdfRect } from "./pdfBearbeitung";

/**
 * Umrechnung Bildschirm (CSS-Pixel relativ zur Seite) ↔ PDF-Userspace (pt,
 * Ursprung unten links). Userspace ist die eine Quelle der Wahrheit für alle
 * Bearbeitungen: unabhängig von Zoom, Ansicht-Drehung und `/Rotate` —
 * genau darauf rechnet der MuPDF-Worker auf dem Server zurück.
 *
 * Der Teil von PDF.js' `PageViewport`, den wir brauchen (die Klasse selbst
 * exportiert pdfjs-dist nicht).
 */
export interface ViewportLike {
    convertToPdfPoint(x: number, y: number): number[];
    convertToViewportRectangle(r: number[]): number[];
}

/** Rundung auf 1/100 pt — genug für jede Anzeige, und Tests bleiben lesbar. */
const r2 = (n: number) => Math.round(n * 100) / 100;

export function normalisiere(r: number[]): PdfRect {
    return [Math.min(r[0], r[2]), Math.min(r[1], r[3]), Math.max(r[0], r[2]), Math.max(r[1], r[3])];
}

/** Zwei CSS-Punkte (relativ zur Seite, beliebige Ecken) → Userspace-Rechteck. */
export function cssZuPdfRect(v: ViewportLike, x0: number, y0: number, x1: number, y1: number): PdfRect {
    // Alle vier Ecken: bei gedrehter Ansicht vertauschen sich die Achsen.
    const ecken = [
        v.convertToPdfPoint(x0, y0),
        v.convertToPdfPoint(x1, y0),
        v.convertToPdfPoint(x0, y1),
        v.convertToPdfPoint(x1, y1),
    ];
    const xs = ecken.map((p) => p[0]);
    const ys = ecken.map((p) => p[1]);
    return [r2(Math.min(...xs)), r2(Math.min(...ys)), r2(Math.max(...xs)), r2(Math.max(...ys))];
}

/** Userspace-Rechteck → CSS-Box relativ zur Seite (für das Overlay). */
export function pdfZuCssRect(v: ViewportLike, r: PdfRect): { left: number; top: number; width: number; height: number } {
    const [a, b, c, d] = v.convertToViewportRectangle(r);
    const left = Math.min(a, c);
    const top = Math.min(b, d);
    return { left: r2(left), top: r2(top), width: r2(Math.abs(c - a)), height: r2(Math.abs(d - b)) };
}

/** Rand um jedes Auswahl-Stück, damit Ober-/Unterlängen sicher mit entfernt werden. */
const RAND_PT = 1;

/**
 * DOMRect-Liste einer Textauswahl (`range.getClientRects()`) → Rechtecke im
 * Userspace. `seitenBox` ist die Inhaltsfläche der Seite im Viewport (ohne den
 * Rahmen des `.page`-Divs). Stücke außerhalb der Seite werden abgeschnitten,
 * leere und unplausibel große verworfen.
 */
export function auswahlZuRechtecken(rects: readonly DOMRect[], seitenBox: DOMRect, v: ViewportLike): PdfRect[] {
    const ergebnis: PdfRect[] = [];
    // Plausibilität: eine Textzeile ist dünn und passt auf die Seite. Kästen
    // ganzer Ebenen oder Seiten (Browser liefern sie bei seitenübergreifender
    // Auswahl mit) würden sonst die ganze Seite löschen. Gemessen wird die
    // DICKE (kürzere Kante) — auf gedrehten Seiten laufen Zeilen senkrecht.
    const maxDicke = 0.1 * Math.max(seitenBox.width, seitenBox.height);
    for (const r of rects) {
        if (Math.min(r.width, r.height) > maxDicke) continue;
        if (r.width > seitenBox.width + 1 || r.height > seitenBox.height + 1) continue;
        const x0 = Math.max(r.left - seitenBox.left, 0);
        const y0 = Math.max(r.top - seitenBox.top, 0);
        const x1 = Math.min(r.left + r.width - seitenBox.left, seitenBox.width);
        const y1 = Math.min(r.top + r.height - seitenBox.top, seitenBox.height);
        if (x1 - x0 <= 0 || y1 - y0 <= 0) continue;
        const [a, b, c, d] = cssZuPdfRect(v, x0, y0, x1, y1);
        ergebnis.push([r2(a - RAND_PT), r2(b - RAND_PT), r2(c + RAND_PT), r2(d + RAND_PT)]);
    }
    return ergebnis;
}

/**
 * Mitte des Teils einer Seite, der im Sichtfenster liegt — in CSS-Pixeln relativ
 * zur Seite (z. B. für ein per Strg+V eingefügtes Bild). Ist nichts sichtbar,
 * die Mitte der Seite.
 */
export function sichtbareMitte(seite: DOMRect, sicht: DOMRect): { cssX: number; cssY: number } {
    const links = Math.max(seite.left, sicht.left);
    const rechts = Math.min(seite.left + seite.width, sicht.left + sicht.width);
    const oben = Math.max(seite.top, sicht.top);
    const unten = Math.min(seite.top + seite.height, sicht.top + sicht.height);
    if (rechts <= links || unten <= oben) return { cssX: r2(seite.width / 2), cssY: r2(seite.height / 2) };
    return { cssX: r2((links + rechts) / 2 - seite.left), cssY: r2((oben + unten) / 2 - seite.top) };
}
